import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { format } from 'date-fns';
import { MapPin, Clock, Users, Car, Calendar, RefreshCw, AlertTriangle, Wind, Activity, Loader2, Edit3, Building2, Home, Bath, Warehouse, X } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import TurbineMapEditor from './TurbineMapEditor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom turbine icons based on status with turbine label
const createTurbineIcon = (status, turbineLabel) => {
  const colors = {
    active: '#22c55e',    // Green - active tasks
    planned: '#eab308',   // Yellow - planned tasks
    problem: '#ef4444',   // Red - problems/alerts
    idle: '#94a3b8'       // Slate - no activity
  };

  // Extract short label (e.g., "T1", "T02", etc.)
  const shortLabel = turbineLabel || '';

  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${colors[status] || colors.idle};
        min-width: 28px;
        height: 28px;
        padding: 0 4px;
        border-radius: 14px;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 10px;
        white-space: nowrap;
      ">${shortLabel}</div>
    `,
    className: 'custom-turbine-marker',
    iconSize: [36, 34],
    iconAnchor: [18, 17]
  });
};

// Custom car icon
const createCarIcon = (isActive = false) => {
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${isActive ? '#3b82f6' : '#64748b'};
        width: 32px;
        height: 32px;
        border-radius: 8px;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
          <circle cx="7" cy="17" r="2"/>
          <circle cx="17" cy="17" r="2"/>
        </svg>
      </div>
      ${isActive ? `
        <div style="
          position: absolute;
          top: -4px;
          right: -4px;
          width: 12px;
          height: 12px;
          background-color: #22c55e;
          border-radius: 50%;
          border: 2px solid white;
          animation: pulse 2s infinite;
        "></div>
      ` : ''}
    `,
    className: 'custom-car-marker',
    iconSize: [38, 38],
    iconAnchor: [19, 19]
  });
};

// Custom facility icons (building, administration, toilet, warehouse)
const createFacilityIcon = (markerType) => {
  const config = {
    building: { color: '#8b5cf6', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>` },
    administration: { color: '#f59e0b', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>` },
    toilet: { color: '#06b6d4', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5"/><line x1="10" x2="8" y1="5" y2="7"/><line x1="2" x2="22" y1="12" y2="12"/><line x1="7" x2="7" y1="19" y2="21"/><line x1="17" x2="17" y1="19" y2="21"/></svg>` },
    warehouse: { color: '#64748b', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/><rect width="12" height="12" x="6" y="10"/></svg>` }
  };

  const { color, icon } = config[markerType] || config.building;

  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        width: 28px;
        height: 28px;
        border-radius: 6px;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
      ">${icon}</div>
    `,
    className: 'custom-facility-marker',
    iconSize: [34, 34],
    iconAnchor: [17, 17]
  });
};

// Add CSS for car marker pulse animation
const carMarkerStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.2); }
  }
  .custom-car-marker {
    z-index: 1000 !important;
  }
`;

// Component to update map view when turbines change
const MapUpdater = ({ turbines, projectMarkers }) => {
  const map = useMap();

  useEffect(() => {
    // Collect all points with coordinates
    const points = [];

    turbines.forEach(t => {
      if (t.latitude && t.longitude) {
        const lat = parseFloat(t.latitude);
        const lng = parseFloat(t.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push([lat, lng]);
        }
      }
    });

    projectMarkers.forEach(m => {
      if (m.latitude && m.longitude) {
        const lat = parseFloat(m.latitude);
        const lng = parseFloat(m.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          points.push([lat, lng]);
        }
      }
    });

    // Fit bounds to all points
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [turbines, projectMarkers, map]);

  return null;
};

const LiveMap = ({ selectedProject, selectedDate, onClose, onRefresh, isModal = false }) => {
  const [turbines, setTurbines] = useState([]);
  const [projectMarkers, setProjectMarkers] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [projectPeople, setProjectPeople] = useState([]);
  const [projectCars, setProjectCars] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mapCenter, setMapCenter] = useState([52.1250, 16.7900]);
  const [mapZoom, setMapZoom] = useState(13);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showMapEditor, setShowMapEditor] = useState(false);

  // Load turbines for selected project
  const loadTurbines = async () => {
    if (!selectedProject) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      // Load project details for name
      const projectResponse = await axios.get(`${API_BASE_URL}/api/projects/${selectedProject}/`, { headers });
      const project = projectResponse.data;
      setProjectName(project.name);

      // Load turbines from ProjectTurbine API
      let projectTurbines = [];
      try {
        const turbinesResponse = await axios.get(`${API_BASE_URL}/api/project-turbines/?project=${selectedProject}`, { headers });
        projectTurbines = turbinesResponse.data.map(turbine => {
          // Build location string: prefer city/country, fallback to GPS coordinates
          let locationStr = project.city || project.country || '';
          if (!locationStr && turbine.latitude && turbine.longitude) {
            locationStr = `${parseFloat(turbine.latitude).toFixed(5)}, ${parseFloat(turbine.longitude).toFixed(5)}`;
          }
          return {
            ...turbine,
            // Ensure consistent field names for compatibility
            name: `${project.type_of_turbine || 'Turbine'} ${turbine.turbine_number}`,
            location: locationStr
          };
        });
      } catch (error) {
        console.log('ProjectTurbine API not available, falling back to project data');
        // Fallback: generate from project.number_of_turbines if API not available
        const numberOfTurbines = parseInt(project.number_of_turbines) || 0;
        const fallbackLocation = project.city || project.country || '';
        projectTurbines = Array.from({ length: numberOfTurbines }, (_, index) => ({
          id: index + 1,
          turbine_number: index + 1,
          turbine_id: `T${index + 1}`,
          model: project.model || '',
          power_rating: project.power_rating || '',
          latitude: null,
          longitude: null,
          name: `${project.type_of_turbine || 'Turbine'} ${index + 1}`,
          location: fallbackLocation
        }));
      }

      setTurbines(projectTurbines);

      // Center map on first turbine with coordinates
      const turbinesWithCoords = projectTurbines.filter(t => t.latitude && t.longitude);
      if (turbinesWithCoords.length > 0) {
        setMapCenter([parseFloat(turbinesWithCoords[0].latitude), parseFloat(turbinesWithCoords[0].longitude)]);
        setMapZoom(12);
      } else if (project.latitude && project.longitude) {
        // Fallback to project location
        setMapCenter([parseFloat(project.latitude), parseFloat(project.longitude)]);
        setMapZoom(12);
      }
    } catch (error) {
      console.error('Error loading turbines:', error);
    }
  };

  // Load task assignments for selected date and project
  const loadTaskAssignments = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_BASE_URL}/api/task-assignments/?project=${selectedProject}&date=${dateString}`,
        { headers }
      );

      setTaskAssignments(response.data);
    } catch (error) {
      console.error('Error loading task assignments:', error);
    }
  };

  // Load project people and cars
  const loadProjectResources = async () => {
    if (!selectedProject) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const [usersResponse, carsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/users/`, { headers }),
        axios.get(`${API_BASE_URL}/api/cars/`, { headers })
      ]);

      setProjectPeople(usersResponse.data);
      setProjectCars(carsResponse.data);
    } catch (error) {
      console.error('Error loading project resources:', error);
    }
  };

  // Load project markers (buildings, administration, toilets, warehouses)
  const loadProjectMarkers = async () => {
    if (!selectedProject) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const response = await axios.get(
        `${API_BASE_URL}/api/project-markers/?project=${selectedProject}`,
        { headers }
      );
      setProjectMarkers(response.data);
    } catch (error) {
      console.error('Error loading project markers:', error);
      setProjectMarkers([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadTurbines(), loadProjectResources(), loadTaskAssignments(), loadProjectMarkers()]);
    setRefreshing(false);
  };

  // Get turbine status based on task assignments
  // turbineNumber = turbine.turbine_number, turbinePkId = turbine.id (ProjectTurbine PK)
  const getTurbineStatus = (turbineNumber, turbinePkId) => {
    // Match by turbine_number OR by turbine FK (ProjectTurbine.id)
    const turbineTasks = taskAssignments.filter(task =>
      task.turbine_id === turbineNumber || task.turbine === turbinePkId
    );
    if (turbineTasks.length === 0) return 'idle';

    const currentTimeStr = format(currentTime, 'HH:mm');
    const activeTasks = turbineTasks.filter(task => {
      return currentTimeStr >= task.start_time && currentTimeStr <= task.end_time;
    });

    return activeTasks.length > 0 ? 'active' : 'planned';
  };

  // Get currently active tasks across all turbines
  const getCurrentActiveTasks = () => {
    const currentTimeStr = format(currentTime, 'HH:mm');

    return taskAssignments.filter(task => {
      return currentTimeStr >= task.start_time && currentTimeStr <= task.end_time;
    }).map(task => {
      // Match turbine by turbine_number OR by turbine FK (ProjectTurbine.id)
      const turbine = turbines.find(t =>
        t.turbine_number === task.turbine_id ||
        t.id === task.turbine_id ||
        t.id === task.turbine
      );
      const assignedPeople = task.assigned_people || [];
      const peopleNames = assignedPeople.map(personId => {
        const person = projectPeople.find(p => p.id === personId);
        return person ? `${person.first_name} ${person.last_name}` : `User ${personId}`;
      });

      const car = task.assigned_car ?
        projectCars.find(c => c.id === task.assigned_car) : null;

      return {
        ...task,
        turbineNumber: turbine ? `T${turbine.turbine_number}${turbine.turbine_id ? ` (${turbine.turbine_id})` : ''}` : `T${task.turbine_id}`,
        turbineLocation: turbine?.location || 'Unknown',
        peopleNames,
        carInfo: car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : null
      };
    });
  };

  const turbineHasCar = (turbineNumber, turbinePkId) => {
    const currentTimeStr = format(currentTime, 'HH:mm');
    const activeTurbineTasks = taskAssignments.filter(task =>
      (task.turbine_id === turbineNumber || task.turbine === turbinePkId) &&
      currentTimeStr >= task.start_time &&
      currentTimeStr <= task.end_time &&
      task.assigned_car
    );
    return activeTurbineTasks.length > 0;
  };

  // Get car locations based on task assignments
  const getCarLocations = () => {
    const currentTimeStr = format(currentTime, 'HH:mm');
    const carLocations = [];

    // Group tasks by car
    const carTasks = {};
    taskAssignments.forEach(task => {
      if (task.assigned_car) {
        if (!carTasks[task.assigned_car]) {
          carTasks[task.assigned_car] = [];
        }
        carTasks[task.assigned_car].push(task);
      }
    });

    // For each car, find its current or next location
    Object.entries(carTasks).forEach(([carId, tasks]) => {
      const car = projectCars.find(c => c.id === parseInt(carId));
      if (!car) return;

      // Sort tasks by start time
      const sortedTasks = tasks.sort((a, b) => a.start_time.localeCompare(b.start_time));

      // Find current active task for this car
      const activeTask = sortedTasks.find(task =>
        currentTimeStr >= task.start_time && currentTimeStr <= task.end_time
      );

      // If no active task, find the next upcoming task or the last completed task
      let currentTask = activeTask;
      if (!currentTask) {
        // Find next task
        const nextTask = sortedTasks.find(task => task.start_time > currentTimeStr);
        // Find last completed task
        const completedTasks = sortedTasks.filter(task => task.end_time < currentTimeStr);
        const lastCompleted = completedTasks.length > 0 ? completedTasks[completedTasks.length - 1] : null;

        currentTask = nextTask || lastCompleted;
      }

      if (currentTask) {
        // Match turbine by turbine_number OR by turbine FK (ProjectTurbine.id)
        const turbine = turbines.find(t =>
          t.turbine_number === currentTask.turbine_id ||
          t.id === currentTask.turbine_id ||
          t.id === currentTask.turbine
        );
        if (turbine && turbine.latitude && turbine.longitude) {
          // Get people assigned to this task
          const assignedPeople = currentTask.assigned_people || [];
          const peopleNames = assignedPeople.map(personId => {
            const person = projectPeople.find(p => p.id === personId);
            return person ? `${person.first_name} ${person.last_name}` : null;
          }).filter(Boolean);

          carLocations.push({
            carId: car.id,
            car,
            turbine,
            task: currentTask,
            isActive: !!activeTask,
            peopleNames,
            // Offset car marker slightly from turbine to avoid overlap
            lat: parseFloat(turbine.latitude) + 0.0003,
            lng: parseFloat(turbine.longitude) + 0.0004
          });
        }
      }
    });

    return carLocations;
  };

  const getTurbineInfo = (turbineNumber, turbinePkId) => {
    // Match by turbine_number OR by turbine FK (ProjectTurbine.id)
    const turbineTasks = taskAssignments.filter(task =>
      task.turbine_id === turbineNumber || task.turbine === turbinePkId
    );

    return turbineTasks.map(task => {
      const assignedPeople = task.assigned_people || [];
      const peopleNames = assignedPeople.map(personId => {
        const person = projectPeople.find(p => p.id === personId);
        return person ? `${person.first_name} ${person.last_name}` : `User ${personId}`;
      });

      const car = task.assigned_car ?
        projectCars.find(c => c.id === task.assigned_car) : null;

      return {
        ...task,
        peopleNames,
        carInfo: car ? `${car.car_brand} ${car.car_model} (${car.plate_number})` : null
      };
    });
  };

  useEffect(() => {
    if (selectedProject) {
      loadTurbines();
      loadProjectResources();
      loadProjectMarkers();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && selectedDate) {
      loadTaskAssignments();
    }
  }, [selectedProject, selectedDate]);

  useEffect(() => {
    setLoading(false);
  }, [turbines, taskAssignments]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const refreshTimer = setInterval(() => {
      if (selectedProject) {
        loadTurbines();
        loadProjectResources();
        loadProjectMarkers();
      }
      if (selectedProject && selectedDate) {
        loadTaskAssignments();
      }
    }, 30000);
    return () => clearInterval(refreshTimer);
  }, [selectedProject, selectedDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 bg-muted/30 rounded-lg border border-border">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading map...</span>
        </div>
      </div>
    );
  }

  const activeTasks = getCurrentActiveTasks();
  const plannedTasks = taskAssignments.filter(task => {
    const currentTimeStr = format(currentTime, 'HH:mm');
    return !(currentTimeStr >= task.start_time && currentTimeStr <= task.end_time);
  });
  const turbinesWithoutGPS = turbines.filter(t => !t.latitude || !t.longitude);
  const carLocations = getCarLocations();
  const activeCars = carLocations.filter(c => c.isActive).length;

  // If editor is open, show only the editor
  if (showMapEditor) {
    return (
      <div style={{ height: 'calc(100vh - 180px)', minHeight: '600px' }}>
        <TurbineMapEditor
          selectedProject={selectedProject}
          onClose={() => {
            setShowMapEditor(false);
            loadTurbines();
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background rounded-lg border border-border overflow-hidden">
      {/* CSS for car marker animations */}
      <style>{carMarkerStyles}</style>

      {/* Header */}
      <div className="px-4 py-3 bg-muted/30 border-b border-border">
        {/* Modal Title Row - only shown when in modal */}
        {isModal && (
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">LIVE Map</h2>
                <p className="text-sm text-muted-foreground">
                  Real-time turbine status and team locations - {format(selectedDate, 'MMM d, yyyy')}
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          {/* Stats */}
          <div className="flex items-center gap-3">
            {/* Project */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-md border border-border">
              <Wind className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{projectName}</span>
            </div>

            {/* Turbines count */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-background rounded-md border border-border">
              <MapPin className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-sm text-muted-foreground">{turbines.length}</span>
            </div>

            {/* Active tasks */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 rounded-md border border-emerald-200">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-emerald-700">{activeTasks.length} active</span>
            </div>

            {/* Planned tasks */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 rounded-md border border-amber-200">
              <div className="w-2 h-2 bg-amber-500 rounded-full" />
              <span className="text-sm font-medium text-amber-700">{plannedTasks.length} planned</span>
            </div>

            {/* Cars on map */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 rounded-md border border-blue-200">
              <Car className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">{carLocations.length} cars</span>
              {activeCars > 0 && (
                <span className="text-xs text-blue-500">({activeCars} active)</span>
              )}
            </div>
          </div>

          {/* Right side - Time & Refresh */}
          <div className="flex items-center gap-3">
            {/* Date */}
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>{format(selectedDate, 'dd.MM.yyyy')}</span>
            </div>

            {/* Live clock */}
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-background rounded-md border border-border">
              <Activity className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-sm font-mono font-medium">{format(currentTime, 'HH:mm:ss')}</span>
            </div>

            {/* Edit Map button */}
            <button
              onClick={() => setShowMapEditor(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Edit Map</span>
            </button>

            {/* Refresh button */}
            <button
              onClick={onRefresh || handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-md border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-emerald-500 rounded-full" />
            <span>Active turbine</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-amber-500 rounded-full" />
            <span>Planned turbine</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-slate-400 rounded-full" />
            <span>No tasks</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center">
              <Car className="w-2.5 h-2.5 text-white" />
            </div>
            <span>Car location</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-4 h-4 bg-blue-500 rounded flex items-center justify-center relative">
              <Car className="w-2.5 h-2.5 text-white" />
              <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-white" />
            </div>
            <span>Car active</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-purple-500 rounded-sm" />
            <span>Building</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-amber-500 rounded-sm" />
            <span>Admin</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div className="w-3 h-3 bg-cyan-500 rounded-sm" />
            <span>Toilet</span>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative" style={{ minHeight: '450px' }}>
        {turbines.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
            <div className="text-center">
              <Wind className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <div className="text-lg font-medium text-foreground mb-1">No turbines found</div>
              <div className="text-sm text-muted-foreground">
                Add turbines with GPS coordinates to this project
              </div>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapUpdater turbines={turbines} projectMarkers={projectMarkers} />

            {/* Turbine markers */}
            {turbines
              .filter(turbine => turbine.latitude && turbine.longitude)
              .map(turbine => {
              // Use both turbine_number and turbine.id (PK) for task matching
              const turbineNumber = turbine.turbine_number;
              const turbinePkId = turbine.id;
              const status = getTurbineStatus(turbineNumber, turbinePkId);
              const turbineInfo = getTurbineInfo(turbineNumber, turbinePkId);

              // Short label for marker: T1, T2, etc.
              const shortLabel = `T${turbine.turbine_number || turbine.id}`;

              return (
                <Marker
                  key={`turbine-${turbine.id}`}
                  position={[parseFloat(turbine.latitude), parseFloat(turbine.longitude)]}
                  icon={createTurbineIcon(status, shortLabel)}
                >
                  <Popup maxWidth={320}>
                    <div className="p-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-3 h-3 rounded-full ${
                          status === 'active' ? 'bg-emerald-500' :
                          status === 'planned' ? 'bg-amber-500' : 'bg-slate-400'
                        }`} />
                        <h3 className="font-semibold text-base">
                          T{turbine.turbine_number || turbine.id}
                          {turbine.turbine_id && <span className="font-normal text-gray-500 ml-1">({turbine.turbine_id})</span>}
                        </h3>
                      </div>

                      {/* Model and Power info */}
                      {(turbine.model || turbine.power_rating) && (
                        <div className="text-xs text-gray-600 mb-2">
                          {turbine.model && <span className="font-medium">{turbine.model}</span>}
                          {turbine.model && turbine.power_rating && <span> · </span>}
                          {turbine.power_rating && <span>{turbine.power_rating} MW</span>}
                        </div>
                      )}

                      <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {turbine.location || 'No location'}
                      </div>

                      {turbineInfo.length > 0 ? (
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                            Today's Tasks
                          </div>
                          {turbineInfo.map((task, index) => (
                            <div key={index} className="bg-gray-50 p-2.5 rounded-lg text-sm border border-gray-100">
                              <div className="font-medium text-gray-900 mb-1.5">{task.subcategory_name || task.subcategory}</div>
                              <div className="flex items-center gap-1 text-gray-600 text-xs mb-1">
                                <Clock className="w-3 h-3" />
                                {task.start_time} - {task.end_time}
                              </div>
                              {task.peopleNames.length > 0 && (
                                <div className="flex items-center gap-1 text-gray-600 text-xs mb-1">
                                  <Users className="w-3 h-3" />
                                  {task.peopleNames.join(', ')}
                                </div>
                              )}
                              {task.carInfo && (
                                <div className="flex items-center gap-1 text-blue-600 text-xs">
                                  <Car className="w-3 h-3" />
                                  {task.carInfo}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 text-center py-2">
                          No tasks scheduled
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Car markers */}
            {carLocations.map(carLoc => (
              <Marker
                key={`car-${carLoc.carId}`}
                position={[carLoc.lat, carLoc.lng]}
                icon={createCarIcon(carLoc.isActive)}
              >
                <Popup maxWidth={300}>
                  <div className="p-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        carLoc.isActive ? 'bg-blue-500' : 'bg-slate-500'
                      }`}>
                        <Car className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base">
                          {carLoc.car.car_brand} {carLoc.car.car_model}
                        </h3>
                        <div className="text-xs text-gray-500">{carLoc.car.plate_number}</div>
                      </div>
                    </div>

                    {/* Status badge */}
                    <div className="mb-3">
                      {carLoc.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                          Currently active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                          Parked / Waiting
                        </span>
                      )}
                    </div>

                    {/* Location info */}
                    <div className="bg-gray-50 p-2.5 rounded-lg text-sm border border-gray-100 mb-2">
                      <div className="flex items-center gap-1 text-gray-600 text-xs mb-1.5">
                        <MapPin className="w-3 h-3" />
                        <span className="font-medium">
                          At T{carLoc.turbine.turbine_number || carLoc.turbine.id}
                          {carLoc.turbine.turbine_id && ` (${carLoc.turbine.turbine_id})`}
                        </span>
                      </div>
                      {carLoc.turbine.location && (
                        <div className="text-xs text-gray-500 ml-4">{carLoc.turbine.location}</div>
                      )}
                    </div>

                    {/* Current task */}
                    {carLoc.task && (
                      <div className="bg-blue-50 p-2.5 rounded-lg text-sm border border-blue-100">
                        <div className="text-xs font-medium text-blue-700 mb-1.5">
                          {carLoc.isActive ? 'Current Task' : 'Next/Last Task'}
                        </div>
                        <div className="font-medium text-gray-900 mb-1">{carLoc.task.subcategory_name || carLoc.task.subcategory}</div>
                        <div className="flex items-center gap-1 text-gray-600 text-xs mb-1">
                          <Clock className="w-3 h-3" />
                          {carLoc.task.start_time} - {carLoc.task.end_time}
                        </div>
                        {carLoc.peopleNames.length > 0 && (
                          <div className="flex items-center gap-1 text-gray-600 text-xs">
                            <Users className="w-3 h-3" />
                            {carLoc.peopleNames.join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Project facility markers (buildings, administration, toilets, warehouses) */}
            {projectMarkers
              .filter(marker => marker.latitude && marker.longitude)
              .map(marker => {
                const typeNames = {
                  building: 'Building',
                  administration: 'Administration',
                  toilet: 'Toilet',
                  warehouse: 'Warehouse'
                };
                return (
                  <Marker
                    key={`facility-${marker.id}`}
                    position={[parseFloat(marker.latitude), parseFloat(marker.longitude)]}
                    icon={createFacilityIcon(marker.marker_type)}
                  >
                    <Popup maxWidth={250}>
                      <div className="p-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="font-semibold text-base">{marker.name}</div>
                        </div>
                        <div className="text-xs text-gray-500 mb-1">
                          Type: {typeNames[marker.marker_type] || marker.marker_type}
                        </div>
                        {marker.description && (
                          <div className="text-xs text-gray-600 mt-2">
                            {marker.description}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
          </MapContainer>
        )}
      </div>

      {/* Warning for turbines without GPS */}
      {turbinesWithoutGPS.length > 0 && (
        <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-200">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-amber-800">
                {turbinesWithoutGPS.length} turbine{turbinesWithoutGPS.length > 1 ? 's' : ''} without GPS coordinates
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {turbinesWithoutGPS.map(turbine => (
                  <span
                    key={turbine.id}
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded"
                    title={turbine.turbine_id || ''}
                  >
                    T{turbine.turbine_number || turbine.id}
                    {turbine.turbine_id && ` (${turbine.turbine_id})`}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LiveMap;
